import { computed, onUpdated, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { isEmpty, isArray, isEqual } from 'lodash'
import { localStorageGet } from 'src/utils/helper/localStorage'
import { isFeatureEnabled } from 'modules/feature-flag/index.js'

import { sideNavigationMenu } from './sideNavigationMenu'
import NavigationTab from '../components/navigation-tab'
import { NON_SIDENAV_ROUTE, SUPPORT_TABS, INVENTORY_INGESTION_HUB_TAB, DROPSHIP_TAB } from '../constants'
import SiteNavigationFooter from '../../fou-side-nav-footer'
import SideNavNudge from '../../side-nav-nudge'
import { getImpersonated } from './helper'
import ArrowIcon from '../components/arrow-icon'
import RectangleTag from '../../fou-navigation/icons/rectangle-tag'
import { partitionFoundriesByContractStatus, isNonActiveStatus } from 'src/utils/helper/contractTermination'
import { MdButton, MdIconAgreements } from '@antiqua-dls/vue-components'
import AnimationProcessing from 'components/animations/processing'
import { useInactiveFamiliesDismiss, useReReleaseSubmit } from 'src/composables/useReactivateFamilies'
import { REACTIVATE_FONT_FAMILIES, AGGREMENTS } from 'src/utils/constants/app-routes-name'
import { adobeCjaEventEmitter } from 'modules/adobe-cja/utils'
import { CJA_EVENTS, LINK_TYPES, LINK_CATEGORIES, LINK_SECTION, LINK_NAMES } from 'modules/adobe-cja/constants'


export default {
    components: {
        SiteNavigationFooter,
        NavigationTab,
        ArrowIcon,
        RectangleTag,
        SideNavNudge,
        MdButton,
        MdIconAgreements,
        AnimationProcessing
    },

    setup(props, { emit }) {

        const store = useStore()
        const route = useRoute()
        const router = useRouter()
        const permissionArr = ref([])
        const isNavBarOpen = ref(localStorage.getItem('isNavBarOpen') !== 'false')
        const user = computed(() => store.getters['auth/user'])
        const hasRole = computed(() => store.getters['auth/hasRole'])
        const getPermissionsArr = computed(() => store.getters['auth/getPermissionsArr'])
        const routeName = computed(() => route.name)
        const isValidNavigation = computed(() => {
            if (isEmpty(routeName.value)) return false
            return !NON_SIDENAV_ROUTE.includes(routeName.value.trim())
        })
        const showHamIcon = computed(() => store.getters['sideNavigation/showHamIcon'])
        const getImpersonateUser = computed(() => {
            return getImpersonated(user.value)
        })
        const sideNavArray = ref(sideNavigationMenu)

        const updateSideNavTab = (title) => {
            sideNavArray.value.forEach((item, i) => {
                if (item.title == title) {
                    item.isOpen = !item.isOpen
                } else {
                    item.isOpen = false
                }
            })
        }

        const isValidPermissionArr = () => {
            if (permissionArr.value.length === 0) {
                permissionArr.value = JSON.parse(window.localStorage.getItem('permissionsArr'))
            }
            return isArray(permissionArr.value) && !isEqual(permissionArr.value.length, 0)
        }
        const getNavigationTabsPermission = tabPermission => {
            let permission = []
            if (getImpersonateUser.value) {
                permission = JSON.parse(window.localStorage.getItem('permissionsArr'))
            } else {
                permission = permissionArr.value
            }
            if (isArray(tabPermission) && tabPermission.length > 0) {
                return tabPermission.some(tabPerm => {
                    return permission.includes(tabPerm)
                })
            }
            return permission.includes(tabPermission)
        }
        const sideNabChecks = (sideNavTab) => {
            if (sideNavTab?.featureName) {
                const _isFeatureEnabled = isFeatureEnabled(sideNavTab.featureName)
                if (!_isFeatureEnabled) {
                    return false;
                }
            }
            if (hasRole.value(['foundrySupport']) && !localStorageGet('FS-Impersonate')) {
                return SUPPORT_TABS.includes(sideNavTab.name)
            }
            else if (hasRole.value(['bulkIngestionUser'])) {
                return INVENTORY_INGESTION_HUB_TAB.includes(sideNavTab.name)
            }
            else if (hasRole.value(['dropShipUser'])) {
                return DROPSHIP_TAB.includes(sideNavTab.name)
            }
            if (!sideNavTab.roles.includes('foundrySupport') || sideNavTab.name === 'Dashboard') return getNavigationTabsPermission(sideNavTab.tabPermission)
        }

        const toggleNavBar = () => {
            isNavBarOpen.value = !isNavBarOpen.value;
            localStorage.setItem('isNavBarOpen', isNavBarOpen.value)
            store.commit('sideNavigation/setSideNav', isNavBarOpen.value)
            emit('isNavBarOpen', isNavBarOpen.value)
            store.commit('inventoryingestion/setNavBarOpen', isNavBarOpen.value)
        }

        const changeHamIconStatus = () => store.dispatch('sideNavigation/changeHamIconStatus', false)

        const profileFoundries = computed(() => store.getters['profile/foundries'])
        const sessionFoundryId = computed(() => store.getters['profile/sessionFoundryId'])
        const { dismissed: nudgeDismissed, dismiss: dismissNudge } = useInactiveFamiliesDismiss()

        const nudgeConfig = computed(() => {
            if (hasRole.value(['foundrySupport', 'FS-Impersonate'])) return null

            const foundries = profileFoundries.value || []
            const i18nBase = 'components.general.side_nav_nudge'

            const sessionFoundry = foundries.find(f => f.id === sessionFoundryId.value)
            if (
                sessionFoundry &&
                !isNonActiveStatus(sessionFoundry.contractStatus) &&
                sessionFoundry.previouslyReleasedFamiliesCount > 0 &&
                !nudgeDismissed.value
            ) {
                return {
                    type: 'previouslyReleased',
                    title: `${i18nBase}.title_previously_released`,
                    description: `${i18nBase}.desc_previously_released`,
                    count: sessionFoundry.previouslyReleasedFamiliesCount,
                    showClose: true,
                    bgColor: '#cde3fd',
                    showCta: true,
                    showReviewCta: true,
                }
            }

            const { terminating, terminated } = partitionFoundriesByContractStatus(foundries)
            if (terminating.length === 0 && terminated.length === 0) return null

            const isSingle = foundries.length === 1
            const isAdmin = [...terminated, ...terminating].some(f => f.isAdmin)

            if (isSingle && !isAdmin) return null


            let targetFoundries, status
            if (terminated.length > 0) {
                targetFoundries = terminated; status = 'terminated'
            } else {
                targetFoundries = terminating; status = 'terminating'
            }

            const isAdminNudge = targetFoundries.some(f => f.isAdmin)
            const foundryName = targetFoundries[0]?.name || ''
            const isMulti = targetFoundries.length > 1
            const titleParams = { name: foundryName, count: targetFoundries.length - 1 }

            let titleKey
            if (status === 'terminated') {
                titleKey = isMulti
                    ? `${i18nBase}.title_multi_terminated`
                    : isAdminNudge
                        ? `${i18nBase}.title_single_terminated_admin`
                        : `${i18nBase}.title_single_terminated_non_admin`
            } else {
                titleKey = isMulti
                    ? `${i18nBase}.title_multi_terminating`
                    : isAdminNudge
                        ? `${i18nBase}.title_single_terminating_admin`
                        : `${i18nBase}.title_single_terminating_non_admin`
            }

            if (isAdminNudge) {
                if (status === 'terminated') {
                    return {
                        title: titleKey,
                        titleParams,
                        description: `${i18nBase}.desc_terminated_admin`,
                        linkPrefix: `${i18nBase}.link_learn_more_prefix`,
                        linkText: `${i18nBase}.link_how_rejoining_works`,
                        linkUrl: 'pages.contract.termination.rejoin_link',
                        linkInline: false,
                        contactAdmin: false,
                        showCta: true
                    }
                }
                return {
                    title: titleKey,
                    titleParams,
                    description: `${i18nBase}.desc_terminating_admin`,
                    linkPrefix: null,
                    linkText: `${i18nBase}.link_contact_support`,
                    linkUrl: 'pages.contract.termination.termination_process_link',
                    linkInline: true,
                    contactAdmin: false,
                    showCta: true
                }
            }

            return {
                title: titleKey,
                titleParams,
                description: status === 'terminated'
                    ? `${i18nBase}.desc_terminated_non_admin`
                    : `${i18nBase}.desc_terminating_non_admin`,
                linkPrefix: null,
                linkText: null,
                linkUrl: null,
                linkInline: false,
                contactAdmin: true,
                showCta: false
            }
        })

        const handleNudgeCtaClick = () => {
            adobeCjaEventEmitter({
                event: CJA_EVENTS.LINK_CLICK,
                links: {
                    linkCategory: LINK_CATEGORIES.SIDE_PANEL_SIGN_LATEST_AGREEMENT,
                    linkSection: LINK_SECTION.SIDE_PANEL,
                    linkName: LINK_NAMES.SIGN_LATEST_AGREEMENT,
                    linkType: LINK_TYPES.BUTTON
                }
            })
            router.push({ name: AGGREMENTS })
        }

        const handleNudgeReviewInventory = () => {
            const foundryId = sessionFoundryId.value
            if (!foundryId) return
            router.push({ name: REACTIVATE_FONT_FAMILIES, params: { foundryId } })
        }

        const { isSubmitting: isNudgeSubmitting, submitReRelease, updateFoundryReleasedCount } = useReReleaseSubmit()

        const handleNudgeReReleaseAll = async () => {
            const foundryId = sessionFoundryId.value
            if (!foundryId) return
            const success = await submitReRelease({ foundryId, all: true })
            if (success) {
                updateFoundryReleasedCount(foundryId, 0)
                store.commit('profile/setReReleaseModal', true)
            }
        }

        const handleNudgeContactSupportClick = () => {
            adobeCjaEventEmitter({
                event: CJA_EVENTS.LINK_CLICK,
                links: {
                    linkCategory: LINK_CATEGORIES.SIDE_PANEL_SIGN_LATEST_AGREEMENT,
                    linkSection: LINK_SECTION.SIDE_PANEL,
                    linkName: LINK_NAMES.CONTACT_FOUNDRY_SUPPORT,
                    linkType: LINK_TYPES.BUTTON
                }
            })
        }

        watch(getPermissionsArr, newPermissionArr => permissionArr.value = newPermissionArr)
        onUpdated(() => {
            sideNavArray.value.forEach((item, i) => {
                if (!item.linkedRoutes.includes(routeName.value)) {
                    item.isOpen = false
                }
            })
        })

        return {
            routeName,
            sideNavArray,
            showHamIcon,
            getImpersonateUser,
            isNavBarOpen,
            user,
            isValidNavigation,
            sideNavigationMenu,
            permissionArr,
            hasRole,
            localStorageGet,
            updateSideNavTab,
            isValidPermissionArr,
            changeHamIconStatus,
            toggleNavBar,
            sideNabChecks,
            getNavigationTabsPermission,
            nudgeConfig,
            nudgeDismissed,
            dismissNudge,
            handleNudgeCtaClick,
            handleNudgeReviewInventory,
            isNudgeSubmitting,
            handleNudgeReReleaseAll,
            handleNudgeContactSupportClick
        }
    }
}
